import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

export interface ExpressionBuilderData {
    expression: string;
    eventName: string;
    eventNames?: string[];
}

@Component({
    selector: 'app-expression-builder-dialog',
    standalone: false,
    template: `
        <div class="section-header" mat-dialog-title>
            <mat-icon class="section-icon">functions</mat-icon>
            <span class="section-title">event rule editor</span>
            <button mat-icon-button class="close-button" mat-dialog-close>
                <mat-icon>close</mat-icon>
            </button>
        </div>
        <div mat-dialog-content style="padding: 8px 16px 0 16px; overflow: hidden">
            <div style="margin-bottom: 8px; font-size: 14px; font-family: monospace; display: flex; align-items: center; gap: 6px">
                <strong style="color: #2196F3">{{ eventName || '?' }}</strong>
                <span>=</span>
                <span style="opacity: 0.9">{{ expression || '...' }}</span>
            </div>
            <div style="margin-bottom: 8px; display: flex; align-items: center; gap: 8px">
                <span style="font-size: 12px; opacity: 0.7">event:</span>
                <input [(ngModel)]="eventName" placeholder="event name"
                       (focus)="activeField = 'event'" (blur)="onEventBlur()"
                       [style.border-color]="activeField === 'event' ? '#2196F3' : 'rgba(255,255,255,0.2)'"
                       style="flex: 1; font-family: monospace; font-size: 14px; padding: 4px 8px; border-radius: 4px;
                              border: 2px solid; background: var(--background-color2); color: #2196F3; font-weight: bold">
            </div>
            <div style="margin-bottom: 4px; display: flex; align-items: flex-start; gap: 8px">
                <span style="font-size: 12px; opacity: 0.7; padding-top: 6px">expression:</span>
            </div>
            <textarea #exprInput
                [(ngModel)]="expression"
                (ngModelChange)="onExpressionChange()"
                (focus)="activeField = 'expression'"
                placeholder="e.g. (door1 || window1) && (temp < 20)"
                rows="3"
                [style.border-color]="!expression.trim() ? 'rgba(255,255,255,0.2)' : (validationError ? '#f44336' : '#4caf50')"
                [style.color]="!expression.trim() ? 'inherit' : (validationError ? '#f44336' : '#4caf50')"
                style="width: 100%; font-family: monospace; font-size: 14px; padding: 8px; border-radius: 4px;
                       border: 2px solid; background: var(--background-color2);
                       resize: vertical; box-sizing: border-box">
            </textarea>
            <div style="margin-top: 12px; display: flex; flex-wrap: wrap; gap: 4px">
                <button *ngFor="let op of operators" mat-stroked-button
                    (click)="insertOp(op, exprInput)"
                    [disabled]="activeField === 'event'"
                    style="min-width: 36px; padding: 0 8px; font-size: 13px; font-family: monospace; line-height: 30px">
                    {{ op }}
                </button>
            </div>
            <div *ngIf="data.eventNames?.length" style="margin-top: 12px">
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px">
                    <span style="font-size: 11px; opacity: 0.5">available events:</span>
                    <input [(ngModel)]="eventFilter" placeholder="filter..."
                           style="flex: 1; font-size: 11px; padding: 2px 6px; border: 1px solid rgba(255,255,255,0.15);
                                  border-radius: 3px; background: transparent; color: inherit">
                </div>
                <div style="display: flex; flex-wrap: wrap; gap: 4px; max-height: 120px; overflow-y: auto; overflow-x: hidden; padding: 2px 0">
                    <span *ngFor="let name of filteredEvents"
                        (click)="onEventChipClick(name, exprInput)"
                        style="padding: 2px 8px; font-size: 11px; color: #4caf50; border: 1px solid #4caf50;
                               border-radius: 3px; cursor: pointer; white-space: nowrap; line-height: 18px"
                        class="event-chip">
                        {{ name }}
                    </span>
                </div>
            </div>
        </div>
        <div style="padding: 0 16px; color: #f44336; font-size: 12px; height: 20px; line-height: 20px; overflow: hidden">
            @if (validationError) {
                <mat-icon style="font-size: 14px; width: 14px; height: 14px; vertical-align: middle; margin-right: 4px">error</mat-icon>
                {{ validationError }}
            }
        </div>
        <div mat-dialog-actions align="end" style="padding: 4px 16px">
            <button mat-button [mat-dialog-close]="null">Cancel</button>
            <button mat-raised-button color="primary" (click)="save()" [disabled]="!!validationError || !expression.trim() || !eventName.trim()">OK</button>
        </div>
    `,
    styles: [`
        :host ::ng-deep {
            .section-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 12px 16px;
                background: var(--background-color2);
                border-left: 4px solid #2196F3;
                margin: 0 0 0 0;

                .section-icon {
                    color: #2196F3;
                    margin-right: 8px;
                    font-size: 18px !important;
                    width: 18px !important;
                    height: 18px !important;
                }

                .section-title {
                    color: var(--text-primary);
                    font-size: 13px;
                    font-weight: 500;
                    text-transform: lowercase;
                    letter-spacing: 0.5px;
                    flex: 1;
                }

                .close-button {
                    opacity: 0.7;
                    transition: all 0.3s ease;
                    &:hover {
                        opacity: 1;
                        transform: rotate(90deg);
                    }
                }
            }
        }
    `]
})
export class ExpressionBuilderDialogComponent {
    expression: string;
    eventName: string;
    validationError = '';
    eventFilter = '';
    activeField: 'event' | 'expression' = 'expression';
    private _history: string[] = [];
    private _historyIndex = -1;
    private _skipHistory = false;
    operators = ['&&', '||', '==', '!=', '<', '>', '<=', '>=', '+', '-', '*', '/', '%', '(', ')', '!', '?', ':',
                 '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'true', 'false', '⎵', '⌫', '↩', '↪', '✕'];

    constructor(
        private dialogRef: MatDialogRef<ExpressionBuilderDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ExpressionBuilderData
    ) {
        this.expression = data.expression || '';
        this.eventName = data.eventName || '';
        if (data.eventNames) {
            data.eventNames = [...data.eventNames].sort((a, b) => a.localeCompare(b));
        }
        this.onExpressionChange();
    }

    get filteredEvents(): string[] {
        if (!this.data.eventNames) return [];
        if (!this.eventFilter) return this.data.eventNames;
        const f = this.eventFilter.toLowerCase();
        return this.data.eventNames.filter(n => n.toLowerCase().includes(f));
    }

    onExpressionChange(): void {
        this.validationError = this.expression.trim() ? this.validate() : '';
        if (!this._skipHistory) {
            if (this._historyIndex < this._history.length - 1) {
                this._history.splice(this._historyIndex + 1);
            }
            this._history.push(this.expression);
            this._historyIndex = this._history.length - 1;
        }
        this._skipHistory = false;
    }

    clear(): void {
        this.expression = '';
        this.onExpressionChange();
    }

    undo(): void {
        if (this._historyIndex > 0) {
            this._historyIndex--;
            this._skipHistory = true;
            this.expression = this._history[this._historyIndex];
            this.validationError = this.expression.trim() ? this.validate() : '';
        }
    }

    redo(): void {
        if (this._historyIndex < this._history.length - 1) {
            this._historyIndex++;
            this._skipHistory = true;
            this.expression = this._history[this._historyIndex];
            this.validationError = this.expression.trim() ? this.validate() : '';
        }
    }

    insertOp(op: string, textarea: HTMLTextAreaElement): void {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        if (op === '↩') { this.undo(); textarea.focus(); return; }
        if (op === '↪') { this.redo(); textarea.focus(); return; }
        if (op === '✕') { this.clear(); textarea.focus(); return; }
        if (op === '⌫') {
            if (start > 0 && start === end) {
                this.expression = this.expression.substring(0, start - 1) + this.expression.substring(end);
                this.onExpressionChange();
                setTimeout(() => { textarea.focus(); textarea.selectionStart = textarea.selectionEnd = start - 1; });
            } else if (start !== end) {
                this.expression = this.expression.substring(0, start) + this.expression.substring(end);
                this.onExpressionChange();
                setTimeout(() => { textarea.focus(); textarea.selectionStart = textarea.selectionEnd = start; });
            }
            return;
        }
        const pad = op === '⎵' ? ' ' : (op.length > 2 ? ` ${op} ` : op);
        this.expression = this.expression.substring(0, start) + pad + this.expression.substring(end);
        this.onExpressionChange();
        setTimeout(() => {
            textarea.focus();
            textarea.selectionStart = textarea.selectionEnd = start + pad.length;
        });
    }

    validate(): string {
        const expr = this.expression.trim();
        if (!expr) return 'Expression cannot be empty';

        // Check balanced parentheses
        let depth = 0;
        for (const ch of expr) {
            if (ch === '(') depth++;
            if (ch === ')') depth--;
            if (depth < 0) return 'Unbalanced parentheses: extra )';
        }
        if (depth > 0) return 'Unbalanced parentheses: missing )';

        // Check ternary balance
        const qCount = (expr.match(/\?/g) || []).length;
        const cCount = (expr.match(/:/g) || []).length;
        if (qCount !== cCount) return 'Unbalanced ternary: each ? needs a :';

        // Check for common mistakes: single & or | (should be && or ||)
        if (/(?<![&])[&](?![&])/.test(expr)) return 'Use && for logical AND (single & is bitwise)';
        if (/(?<![|])[|](?![|])/.test(expr)) return 'Use || for logical OR (single | is bitwise)';

        // Check for assignment = instead of comparison ==
        if (/(?<![=!<>])=(?!=)/.test(expr)) return 'Use == for comparison (single = is assignment)';

        // Try parsing by replacing event names with dummy values
        try {
            const testExpr = expr.replace(/[a-zA-Z_][a-zA-Z0-9_]*/g, '1');
            new Function(`return (${testExpr})`);
        } catch (e: any) {
            return 'Syntax error: ' + (e.message || 'invalid expression');
        }

        return '';
    }

    onEventChipClick(name: string, textarea: HTMLTextAreaElement): void {
        if (this.activeField === 'event') {
            this.eventName = name;
        } else {
            this.insertOp(name, textarea);
        }
    }

    onEventBlur(): void {
        setTimeout(() => {
            if (this.activeField === 'event') {
                this.activeField = 'expression';
            }
        }, 200);
    }

    save(): void {
        this.validationError = this.validate();
        if (this.validationError) return;
        this.dialogRef.close({ eventName: this.eventName.trim(), expression: this.expression.trim() });
    }
}
